import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight, RotateCcw, Cog } from "lucide-react";
import axios from "axios";
import EngineCard from "@/components/EngineCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Catalog() {
  const [meta, setMeta] = useState({ total: 0, brands: [], fuels: [], types: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [fuel, setFuel] = useState("");
  const [type, setType] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const sectionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/engines/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: 24, sort };
    if (debouncedSearch) params.search = debouncedSearch;
    if (brand) params.brand = brand;
    if (fuel) params.fuel = fuel;
    if (type) params.engine_type = type;
    axios
      .get(`${API}/engines`, { params })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debouncedSearch, brand, fuel, type, sort, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, brand, fuel, type, sort]);

  const reset = () => {
    setSearch("");
    setBrand("");
    setFuel("");
    setType("");
    setSort("newest");
    setPage(1);
  };

  const changePage = (p) => {
    setPage(p);
    if (sectionRef.current) {
      const y = sectionRef.current.getBoundingClientRect().top + window.scrollY - 70;
      window.__lenis ? window.__lenis.scrollTo(y) : window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const selectCls =
    "mono w-full border border-[#272A38] bg-[#0B0C10] px-3 py-3 text-xs uppercase tracking-widest text-[#F3F4F6] transition-colors duration-300 hover:border-[#F59E0B]/60";

  return (
    <section id="catalogo" ref={sectionRef} data-testid="catalog-section" className="relative border-b border-[#272A38] bg-[#0B0C10] py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="mono mb-3 text-xs uppercase tracking-[0.3em] text-[#F59E0B]">02 — Catalogo</p>
            <h2 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F3F4F6] sm:text-6xl">
              Il magazzino <span className="text-stroke-amber italic">motori</span>
            </h2>
          </div>
          <p className="mono text-xs uppercase tracking-widest text-[#6B7280]" data-testid="catalog-result-count">
            {loading ? "Ricerca in corso…" : `${data.total.toLocaleString("it-IT")} motori trovati`}
          </p>
        </motion.div>

        <div className="mb-10 grid grid-cols-1 gap-3 border border-[#272A38] bg-[#12141C] p-4 sm:grid-cols-2 lg:grid-cols-6" data-testid="catalog-filters">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="catalog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Codice motore, modello, marca…"
              className="mono w-full border border-[#272A38] bg-[#0B0C10] py-3 pl-10 pr-3 text-xs uppercase tracking-widest text-[#F3F4F6] placeholder:text-[#6B7280]"
            />
          </div>
          <select data-testid="catalog-brand-filter" value={brand} onChange={(e) => setBrand(e.target.value)} className={selectCls}>
            <option value="">Tutte le marche</option>
            {meta.brands.map((b) => (
              <option key={b.name} value={b.name}>
                {b.name} ({b.count})
              </option>
            ))}
          </select>
          <select data-testid="catalog-fuel-filter" value={fuel} onChange={(e) => setFuel(e.target.value)} className={selectCls}>
            <option value="">Carburante</option>
            {meta.fuels.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <select data-testid="catalog-type-filter" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
            <option value="">Tipologia</option>
            {meta.types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <div className="flex gap-3">
            <select data-testid="catalog-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
              <option value="newest">Più recenti</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
              <option value="cv_desc">Più potenti</option>
            </select>
            <button
              data-testid="catalog-reset-filters"
              onClick={reset}
              aria-label="Azzera filtri"
              className="flex items-center justify-center border border-[#272A38] bg-[#0B0C10] px-3 text-[#9CA3AF] transition-colors duration-300 hover:border-[#F59E0B] hover:text-[#F59E0B]"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-24" data-testid="catalog-loading">
            <Cog className="h-10 w-10 animate-spin text-[#F59E0B]" style={{ animationDuration: "2.5s" }} />
            <p className="mono text-xs uppercase tracking-widest text-[#6B7280]">Caricamento motori…</p>
          </div>
        ) : data.items.length === 0 ? (
          <div className="border border-[#272A38] bg-[#12141C] py-24 text-center" data-testid="catalog-empty-state">
            <p className="font-display text-3xl font-bold uppercase text-[#9CA3AF]">Nessun motore trovato</p>
            <p className="mono mt-2 text-xs uppercase tracking-widest text-[#6B7280]">
              Prova a modificare la ricerca o contattaci su WhatsApp
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3" data-testid="catalog-grid">
            {data.items.map((engine, i) => (
              <EngineCard key={engine.sku} engine={engine} index={i} />
            ))}
          </div>
        )}

        {data.pages > 1 && !loading && (
          <div className="mt-12 flex items-center justify-center gap-3" data-testid="catalog-pagination">
            <button
              data-testid="catalog-prev-page"
              onClick={() => changePage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex h-11 w-11 items-center justify-center border border-[#272A38] text-[#9CA3AF] transition-colors duration-300 hover:border-[#F59E0B] hover:text-[#F59E0B] disabled:opacity-30"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="mono px-4 text-xs uppercase tracking-widest text-[#9CA3AF]" data-testid="catalog-page-indicator">
              {page} / {data.pages}
            </span>
            <button
              data-testid="catalog-next-page"
              onClick={() => changePage(Math.min(data.pages, page + 1))}
              disabled={page === data.pages}
              className="flex h-11 w-11 items-center justify-center border border-[#272A38] text-[#9CA3AF] transition-colors duration-300 hover:border-[#F59E0B] hover:text-[#F59E0B] disabled:opacity-30"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
